import type { VehicleAvailabilityWithSupplier } from "@apac/components/cars";
import type { GroundPriceQuote, ShopResponse } from "@b2bportal/ground-api";
import {
  type FiatPrice,
  type Prices,
  Product,
  type QuoteBreakdown,
} from "@b2bportal/purchase-api";
import type { CartContext } from "@checkout/states";
import { ParentState } from "@checkout/types";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import type { State } from "xstate";
import { groundShopKey, type GroundStateWithoutValue } from "./types";

export const getSelectedVehicleAvailability = (
  state: GroundStateWithoutValue
): VehicleAvailabilityWithSupplier =>
  state.context[groundShopKey].selectedVehicle;

export const getShopRentalDays = (state: GroundStateWithoutValue) => {
  const vehicle = state.context[groundShopKey].selectedVehicle;
  if (vehicle == null) return undefined;

  const {
    pickUp: { time: pickupDateTime },
    dropOff: { time: dropoffDateTime },
  } = vehicle;
  return calculateDays(pickupDateTime, dropoffDateTime);
};

export const getShopResponse = (state: GroundStateWithoutValue): ShopResponse =>
  state.context[groundShopKey].shop;

export const getOpaqueBookRequest = (state: GroundStateWithoutValue): string =>
  state.context[groundShopKey].shop.opaqueBookRequest;

export const getQuoteBreakdown = (
  state: GroundStateWithoutValue
): QuoteBreakdown | undefined =>
  state.context[ParentState.cartQuote]?.quoteBreakdown;

export const getQuotedGroundProduct = (
  state: Pick<State<CartContext>, "context">
): GroundPriceQuote | undefined => {
  const breakdown = state.context[ParentState.cartQuote].quoteBreakdown;
  const groundProduct = breakdown?.products?.find(
    (p) => p.product.type === Product.Ground
  );
  return groundProduct?.product?.value;
};

export const getQuotedTotalDays = createSelector(
  getQuotedGroundProduct,
  (product) => {
    if (product?.vehAvail == null) return undefined;

    const {
      pickUp: { dateTime: pickupDateTime },
      dropOff: { dateTime: dropoffDateTime },
    } = product.vehAvail;
    return calculateDays(pickupDateTime, dropoffDateTime);
  }
);

export const getTrackingProperties = (state: GroundStateWithoutValue) =>
  state.context[groundShopKey].trackingProperties;

export const calculateDays = (from: string, to: string) => {
  if (from != null && to != null) {
    return dayjs(to).diff(dayjs(from), "days");
  } else return undefined;
};

export type CarPriceBreakdownItems = {
  payNowTotal: Prices;
  carPrice: FiatPrice;
  taxesAndFees: FiatPrice;
  payLaterTotalLocalCurrency: FiatPrice;
  totalCharge: Prices;
  rentDays?: number;
};

export const getCarBreakdownItems = (
  state: GroundStateWithoutValue
): CarPriceBreakdownItems => {
  const quotedGroundProduct = getQuotedGroundProduct(state)?.vehAvail;
  const quotedRentDays = getQuotedTotalDays(state);
  const quotedPricing = quotedGroundProduct?.rentalRate;

  // data from quote breakdown
  if (quotedPricing) {
    return {
      payNowTotal: quotedPricing.payNow,
      payLaterTotalLocalCurrency: quotedPricing.payAtDesk,
      carPrice: quotedPricing.base,
      taxesAndFees: quotedPricing.feesSummary.total,
      totalCharge: quotedPricing.totalCharge,
      rentDays: quotedRentDays,
    };
    // data from shopping
  } else {
    const selectedVehicle = getSelectedVehicleAvailability(state);
    const shopRentalDays = getShopRentalDays(state);

    return {
      payNowTotal: selectedVehicle.payNow,
      payLaterTotalLocalCurrency: selectedVehicle.payAtDesk,
      carPrice: selectedVehicle.baseRate,
      taxesAndFees: selectedVehicle.fees,
      totalCharge: selectedVehicle.totalCharge,
      rentDays: shopRentalDays,
    };
  }
};
